import { useMemo } from 'react';

import { normalizeValue, shouldUseLogScale } from './scaleUtils';

import type { StudentCluster, StudentQuadrantData } from '../types';
import type { ScaleType } from './scaleUtils';

type UseQuadrantPositionsProps = {
  avatarSize: number;
  chartHeight: number;
  chartWidth: number;
  students: StudentQuadrantData[];
};

type UseQuadrantPositionsReturn = {
  clusters: StudentCluster[];
  maxMessages: number;
  maxQualityScore: number;
  xScale: ScaleType;
  yScale: ScaleType;
};

function useQuadrantPositions({ avatarSize, chartHeight, chartWidth, students }: UseQuadrantPositionsProps): UseQuadrantPositionsReturn {
  return useMemo(() => {
    if (students.length === 0 || chartWidth === 0 || chartHeight === 0) {
      return { clusters: [], maxMessages: 0, maxQualityScore: 0, xScale: 'linear', yScale: 'linear' };
    }

    const CHART_PADDING = 32;
    const maxMessages = Math.max(...students.map((s) => s.totalMessages), 1);
    const maxQualityScore = Math.max(...students.map((s) => s.qualityScore), 1);
    const halfAvatar = avatarSize / 2;
    const edgeOffset = halfAvatar + CHART_PADDING;

    const xScale = shouldUseLogScale(students.map((s) => s.qualityScore)) ? 'log' : 'linear';
    const yScale = shouldUseLogScale(students.map((s) => s.totalMessages)) ? 'log' : 'linear';

    const positioned = students.map((student) => ({
      student,
      x: edgeOffset + normalizeValue(student.qualityScore, maxQualityScore, xScale) * (chartWidth - edgeOffset * 2),
      y: edgeOffset + (1 - normalizeValue(student.totalMessages, maxMessages, yScale)) * (chartHeight - edgeOffset * 2),
    }));

    const clusters: StudentCluster[] = [];

    positioned.forEach(({ student, x, y }) => {
      let merged = false;

      for (const cluster of clusters) {
        const dx = cluster.x - x;
        const dy = cluster.y - y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < halfAvatar) {
          cluster.students.push(student);
          merged = true;
          break;
        }
      }

      if (!merged) {
        clusters.push({ students: [student], x, y });
      }
    });

    return { clusters, maxMessages, maxQualityScore, xScale, yScale };
  }, [avatarSize, chartHeight, chartWidth, students]);
}

export default useQuadrantPositions;
